import { Chess } from 'chess.js'
import type { Color, Fen, SquareName, UciMove } from '@shess/schemas'

export interface UciParts {
  from: SquareName
  to: SquareName
  promotion?: 'q' | 'r' | 'b' | 'n'
}

export function parseUci(move: UciMove): UciParts {
  const from = move.slice(0, 2) as SquareName
  const to = move.slice(2, 4) as SquareName
  const promotion = move.slice(4, 5)
  return promotion
    ? { from, to, promotion: promotion as UciParts['promotion'] }
    : { from, to }
}

export function toUci(parts: UciParts): UciMove {
  return `${parts.from}${parts.to}${parts.promotion ?? ''}`
}

export function colorOf(turn: 'w' | 'b'): Color {
  return turn === 'w' ? 'white' : 'black'
}

/** Applies UCI moves in order. Throws if any move is illegal. */
export function applyMoves(startFen: Fen, moves: readonly UciMove[]): Chess {
  const board = new Chess(startFen)
  for (const move of moves) {
    board.move(parseUci(move))
  }
  return board
}

export function fenAfter(startFen: Fen, moves: readonly UciMove[]): Fen {
  return applyMoves(startFen, moves).fen()
}

/**
 * Whether a legal move in this position ends the game by checkmate. Used to
 * accept alternate mating moves, the way Lichess does.
 */
export function isMatingMove(fenBefore: Fen, move: UciMove): boolean {
  const board = new Chess(fenBefore)
  try {
    board.move(parseUci(move))
  } catch {
    return false
  }
  return board.isCheckmate()
}

export function isLegalMove(fenBefore: Fen, move: UciMove): boolean {
  const board = new Chess(fenBefore)
  try {
    board.move(parseUci(move))
    return true
  } catch {
    return false
  }
}
